'use client';

import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { sendGTMEvent } from '../lib/sendGTMEvent';

export type GTMReadCardsEvent = 'read_review' | 'read_question';
export type GTMReadCardsCategory = 'preview' | 'view_all';

const DEBOUNCE_MS = 100;

export function useGTMReadCards(
  event: GTMReadCardsEvent,
  category: GTMReadCardsCategory,
  itemsLength: number
) {
  const pathname = usePathname();
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const ref = useCallback((node: HTMLElement | null) => setRoot(node), []);

  const seenRef = useRef(new WeakSet<Element>());
  const batchRef = useRef(0);
  const timerRef = useRef(-1);

  useEffect(() => {
    seenRef.current = new WeakSet();
    batchRef.current = 0;
    window.clearTimeout(timerRef.current);
  }, [pathname, event, category]);

  useEffect(() => {
    if (!root || !itemsLength) return;

    const flush = () => {
      const count = batchRef.current;
      batchRef.current = 0;
      if (!count) return;

      sendGTMEvent({
        event,
        eventCategory: category,
        eventAction: String(count)
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        let added = false;

        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
          if (seenRef.current.has(entry.target)) continue;

          seenRef.current.add(entry.target);
          observer.unobserve(entry.target);
          batchRef.current += 1;
          added = true;
        }

        if (!added) return;

        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(flush, DEBOUNCE_MS);
      },
      { threshold: 0.5 }
    );

    const targets = root.querySelectorAll('[data-gtm-read-target]');
    const nodes =
      targets.length > 0 ? Array.from(targets) : Array.from(root.children);

    nodes.forEach((el) => {
      if (!seenRef.current.has(el)) observer.observe(el);
    });

    return () => {
      window.clearTimeout(timerRef.current);
      flush();
      observer.disconnect();
    };
  }, [root, itemsLength, pathname, event, category]);

  return ref;
}
